import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import LocalShipping from "@material-ui/icons/LocalShipping";
import ArrowBack from "@material-ui/icons/ArrowBack";
import Save from "@material-ui/icons/Save";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);

const useFormStyles = makeStyles({
  section: { marginBottom: "24px" },
  sectionTitle: {
    fontSize: "13px", fontWeight: 700, color: "#555", textTransform: "uppercase",
    letterSpacing: "0.5px", marginBottom: "12px", paddingBottom: "6px",
    borderBottom: "1px solid #f0f0f0",
  },
  row: { display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "12px" },
  field: { display: "flex", flexDirection: "column", gap: "4px", minWidth: "180px", flex: 1 },
  label: { fontSize: "12px", color: "#888", fontWeight: 500 },
  input: {
    border: "1px solid #ddd", borderRadius: "6px", padding: "8px 12px",
    fontSize: "14px", color: "#333", outline: "none", width: "100%",
    boxSizing: "border-box", backgroundColor: "#fff",
    "&:focus": { borderColor: "#1976d2" },
  },
  select: {
    border: "1px solid #ddd", borderRadius: "6px", padding: "8px 12px",
    fontSize: "14px", color: "#333", width: "100%", backgroundColor: "#fff",
  },
  infoBox: {
    backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "14px 16px",
    marginBottom: "20px", fontSize: "13px", color: "#555",
    display: "flex", gap: "24px", flexWrap: "wrap",
  },
  infoItem: { display: "flex", flexDirection: "column", gap: "2px" },
  infoLabel: { fontSize: "11px", color: "#aaa", fontWeight: 500, textTransform: "uppercase" },
  infoValue: { fontSize: "14px", fontWeight: 600, color: "#333" },
  actions: { display: "flex", gap: "12px", marginTop: "16px" },
  errorBox: {
    backgroundColor: "#fce4ec", color: "#b71c1c", borderRadius: "6px",
    padding: "10px 14px", marginBottom: "16px", fontSize: "13px",
  },
  successBox: {
    backgroundColor: "#e8f5e9", color: "#2e7d32", borderRadius: "6px",
    padding: "10px 14px", marginBottom: "16px", fontSize: "13px",
  },
});

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

function gql(query, variables) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
    credentials: "include",
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const DELIVERY_STATUS_OPTIONS = [
  { value: "", label: "— Not set —" },
  { value: "Pending", label: "Pending" },
  { value: "Shipping", label: "Shipping" },
  { value: "Delivered", label: "Delivered" },
  { value: "Failed", label: "Failed" },
  { value: "Returned", label: "Returned" },
];

const INVOICE_STATUS_OPTIONS = [
  { value: "Processing", label: "Processing" },
  { value: "Paid", label: "Paid" },
  { value: "Shipped", label: "Shipped" },
  { value: "Completed", label: "Completed" },
  { value: "Returned", label: "Returned" },
  { value: "Cancelled", label: "Cancelled" },
];

export default function EditInvoicePage() {
  const classes = useStyles();
  const form = useFormStyles();
  const { id } = useParams();
  const history = useHistory();

  const [invoice, setInvoice] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const [fields, setFields] = React.useState({
    shippingNumber: "", carrier: "", deliveryFee: "",
    deliveryNote: "", shippingNotes: "", deliveryTime: "",
    deliveryStatus: "", receiverName: "", receiverPhone: "",
    receiverAddress: "", receiverProvince: "", receiverWard: "",
    weight: "", length: "", width: "", height: "",
    status: "Processing",
  });

  React.useEffect(() => {
    gql(`query Q($id:String!){getInvoice(id:$id){
      id invoiceNumber invoiceDate status
      customer { name customerNumber }
      subtotal discount total paid
      shippingNumber carrier deliveryFee deliveryNote shippingNotes
      deliveryTime deliveryStatus
      receiverName receiverPhone receiverAddress receiverProvince receiverWard
      weight length width height
    }}`, { id })
      .then(({ data, errors }) => {
        if (errors) { setError(errors[0].message); return; }
        const inv = data?.getInvoice;
        if (!inv) { setError("Invoice not found"); return; }
        setInvoice(inv);
        setFields({
          shippingNumber: inv.shippingNumber || "",
          carrier: inv.carrier || "",
          deliveryFee: inv.deliveryFee != null ? String(inv.deliveryFee) : "",
          deliveryNote: inv.deliveryNote || "",
          shippingNotes: inv.shippingNotes || "",
          deliveryTime: toDatetimeLocal(inv.deliveryTime),
          deliveryStatus: inv.deliveryStatus || "",
          receiverName: inv.receiverName || "",
          receiverPhone: inv.receiverPhone || "",
          receiverAddress: inv.receiverAddress || "",
          receiverProvince: inv.receiverProvince || "",
          receiverWard: inv.receiverWard || "",
          weight: inv.weight != null ? String(inv.weight) : "",
          length: inv.length != null ? String(inv.length) : "",
          width: inv.width != null ? String(inv.width) : "",
          height: inv.height != null ? String(inv.height) : "",
          status: inv.status || "Processing",
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = () => {
    setError(""); setSuccess("");
    setSaving(true);

    const input = {
      id,
      shippingNumber: fields.shippingNumber || null,
      carrier: fields.carrier || null,
      deliveryFee: fields.deliveryFee !== "" ? parseFloat(fields.deliveryFee) : null,
      deliveryNote: fields.deliveryNote || null,
      shippingNotes: fields.shippingNotes || null,
      deliveryTime: fields.deliveryTime || null,
      deliveryStatus: fields.deliveryStatus || null,
      receiverName: fields.receiverName || null,
      receiverPhone: fields.receiverPhone || null,
      receiverAddress: fields.receiverAddress || null,
      receiverProvince: fields.receiverProvince || null,
      receiverWard: fields.receiverWard || null,
      weight: fields.weight !== "" ? parseFloat(fields.weight) : null,
      length: fields.length !== "" ? parseFloat(fields.length) : null,
      width: fields.width !== "" ? parseFloat(fields.width) : null,
      height: fields.height !== "" ? parseFloat(fields.height) : null,
      status: fields.status,
    };

    gql(`mutation M($input:UpdateDeliveryDto!){updateInvoiceDelivery(input:$input){id status}}`, { input })
      .then(({ data, errors }) => {
        if (errors) { setError(errors[0].message); return; }
        if (data?.updateInvoiceDelivery) {
          setSuccess("Invoice updated successfully.");
          setInvoice((inv) => ({ ...inv, status: data.updateInvoiceDelivery.status }));
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "60px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (invoice && ["Completed", "Returned", "Cancelled"].includes(invoice.status)) {
    return (
      <GridContainer>
        <GridItem xs={12} md={8} lg={6} style={{ margin: "0 auto" }}>
          <Card>
            <CardBody>
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔒</div>
                <h4 style={{ color: "#555" }}>Cannot edit this invoice</h4>
                <p style={{ color: "#888", fontSize: "14px" }}>
                  Invoices with status <strong>{invoice.status}</strong> cannot be edited.
                </p>
                <Button variant="outlined" startIcon={<ArrowBack />}
                  onClick={() => history.push("/admin/invoices")} style={{ marginTop: "16px" }}>
                  Back to Invoices
                </Button>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <GridItem xs={12} md={10} lg={8} style={{ margin: "0 auto" }}>
        <Card>
          <CardHeader color="warning" icon>
            <CardIcon color="warning"><LocalShipping /></CardIcon>
            <h4 className={classes.cardIconTitle}>
              Edit Delivery — {invoice?.invoiceNumber}
            </h4>
          </CardHeader>
          <CardBody>
            {/* Invoice info summary */}
            {invoice && (
              <div className={form.infoBox}>
                <div className={form.infoItem}>
                  <span className={form.infoLabel}>Invoice</span>
                  <span className={form.infoValue}>{invoice.invoiceNumber}</span>
                </div>
                <div className={form.infoItem}>
                  <span className={form.infoLabel}>Date</span>
                  <span className={form.infoValue}>{formatDateTime(invoice.invoiceDate)}</span>
                </div>
                <div className={form.infoItem}>
                  <span className={form.infoLabel}>Customer</span>
                  <span className={form.infoValue}>{invoice.customer?.name || "—"}</span>
                </div>
                <div className={form.infoItem}>
                  <span className={form.infoLabel}>Total</span>
                  <span className={form.infoValue}>{Number(invoice.total).toLocaleString("vi-VN")}</span>
                </div>
              </div>
            )}

            {error && <div className={form.errorBox}>{error}</div>}
            {success && <div className={form.successBox}>{success}</div>}

            {/* Receiver info */}
            <div className={form.section}>
              <div className={form.sectionTitle}>Receiver information</div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Receiver name</span>
                  <input className={form.input} value={fields.receiverName} onChange={set("receiverName")} placeholder="Full name" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Phone</span>
                  <input className={form.input} value={fields.receiverPhone} onChange={set("receiverPhone")} placeholder="Phone number" />
                </div>
              </div>
              <div className={form.row}>
                <div className={form.field} style={{ flex: 2 }}>
                  <span className={form.label}>Address</span>
                  <input className={form.input} value={fields.receiverAddress} onChange={set("receiverAddress")} placeholder="Street address" />
                </div>
              </div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Province / City</span>
                  <input className={form.input} value={fields.receiverProvince} onChange={set("receiverProvince")} placeholder="Province" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Ward / District</span>
                  <input className={form.input} value={fields.receiverWard} onChange={set("receiverWard")} placeholder="Ward" />
                </div>
              </div>
            </div>

            {/* Shipping */}
            <div className={form.section}>
              <div className={form.sectionTitle}>Shipping</div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Tracking / Shipping number</span>
                  <input className={form.input} value={fields.shippingNumber} onChange={set("shippingNumber")} placeholder="e.g. VN123456789" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Carrier</span>
                  <input className={form.input} value={fields.carrier} onChange={set("carrier")} placeholder="e.g. GHN, GHTK, ViettelPost" />
                </div>
              </div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Delivery fee</span>
                  <input className={form.input} type="number" min="0" value={fields.deliveryFee} onChange={set("deliveryFee")} placeholder="0" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Expected delivery time</span>
                  <input className={form.input} type="datetime-local" value={fields.deliveryTime} onChange={set("deliveryTime")} />
                </div>
              </div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Delivery status</span>
                  <Select value={fields.deliveryStatus} onChange={set("deliveryStatus")} disableUnderline className={form.select}>
                    {DELIVERY_STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Package dimensions */}
            <div className={form.section}>
              <div className={form.sectionTitle}>Package dimensions</div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Weight (kg)</span>
                  <input className={form.input} type="number" min="0" step="0.001" value={fields.weight} onChange={set("weight")} placeholder="0.000" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Length (cm)</span>
                  <input className={form.input} type="number" min="0" step="0.1" value={fields.length} onChange={set("length")} placeholder="0" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Width (cm)</span>
                  <input className={form.input} type="number" min="0" step="0.1" value={fields.width} onChange={set("width")} placeholder="0" />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Height (cm)</span>
                  <input className={form.input} type="number" min="0" step="0.1" value={fields.height} onChange={set("height")} placeholder="0" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className={form.section}>
              <div className={form.sectionTitle}>Notes</div>
              <div className={form.row}>
                <div className={form.field}>
                  <span className={form.label}>Delivery note</span>
                  <textarea className={form.input} value={fields.deliveryNote} onChange={set("deliveryNote")}
                    placeholder="Note for delivery person..." rows={2} style={{ resize: "vertical" }} />
                </div>
                <div className={form.field}>
                  <span className={form.label}>Shipping notes</span>
                  <textarea className={form.input} value={fields.shippingNotes} onChange={set("shippingNotes")}
                    placeholder="Internal shipping notes..." rows={2} style={{ resize: "vertical" }} />
                </div>
              </div>
            </div>

            {/* Invoice status */}
            <div className={form.section}>
              <div className={form.sectionTitle}>Invoice status</div>
              <div className={form.row}>
                <div className={form.field} style={{ maxWidth: "240px" }}>
                  <span className={form.label}>Status</span>
                  <Select value={fields.status} onChange={set("status")} disableUnderline className={form.select}>
                    {INVOICE_STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={form.actions}>
              <Button variant="contained" color="primary" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save changes"}
              </Button>
              <Button variant="outlined" startIcon={<ArrowBack />}
                onClick={() => history.push("/admin/invoices")}>
                Back
              </Button>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
